import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { Observable, Subject, Subscription, forkJoin, of, takeUntil } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LookupTypeCodes } from '../../../models/lookup.models';
import {
  EmployeePracticeAreaSource,
  SYSTEM_DEFAULT_VISIBILITY_CODE,
  Permission,
  Role,
  UserDto,
  UserListRequest,
} from '../../../models/user.models';
import { AuthService } from '../../../services/auth.service';
import { LookupService } from '../../../services/lookup.service';
import { ResourceService } from '../../../services/resource.service';
import { RoleService } from '../../../services/role.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { UserService } from '../../../services/user.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import {
  MembershipChipItem,
  MembershipChipsComponent,
} from '../../shared/membership-chips/membership-chips.component';
import {
  ResourceVisibilityDialogComponent,
  ResourceVisibilityDialogData,
} from '../../shared/resource-visibility-dialog/resource-visibility-dialog.component';
import { UserOfficesDialogComponent } from '../../shared/user-offices-dialog/user-offices-dialog.component';
import { UserPracticeAreasDialogComponent } from '../../shared/user-practice-areas-dialog/user-practice-areas-dialog.component';
import { describeApiError } from '../../../utils/api-error.utils';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    RouterModule,
    ErrorStateComponent,
    MembershipChipsComponent,
  ],
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class UsersComponent implements OnInit, OnDestroy {
  private userService = inject(UserService);
  private resourceService = inject(ResourceService);
  private lookupService = inject(LookupService);
  private roleService = inject(RoleService);
  private authService = inject(AuthService);
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  snackbarService = inject(SnackbarService);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  readonly sort = viewChild(MatSort);

  // Subscription management
  private destroy$ = new Subject<void>();
  private pendingSearchRequest: Subscription | null = null;

  // Data properties
  users: UserDto[] = [];
  roles: Role[] = [];
  isLoading = false;
  isSearching = false;
  error: string | null = null;
  isCurrentUserAdmin = false;
  canManagePermissions = false; // Track if user can manage permissions (Super Admin only)
  updatingUserRoles = new Set<number>(); // Track which users are having their roles updated
  totalSuperAdminCount = 0; // Total active Super Admins across the whole system (not just the current page)

  // Pagination
  totalCount = 0;
  pageSize = 10;
  pageIndex = 0;

  // Search
  searchQuery = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Table configuration
  private readonly baseColumns: string[] = [
    'name',
    'practiceAreas',
    'offices',
    'visibility',
    'roles',
    'lastLogin',
    'status',
  ];
  displayedColumns: string[] = [...this.baseColumns];

  dataSource = new MatTableDataSource<UserDto>([]);

  // Display names for RESOURCE_VISIBILITY_MODE codes, keyed by code, loaded from
  // the backend lookup so the table and snackbar labels match the visibility dialog.
  private visibilityModeNames = new Map<string, string>();

  // Label of the system-default mode (the lookup value flagged isDefault), used for the
  // Visibility column when a resource has no explicit override. Derived from the lookup rather
  // than hardcoded so it tracks the backend default.
  private visibilityDefaultLabel = '';

  // Filter form
  filterForm: FormGroup;

  // Current request state
  currentRequest: UserListRequest = {
    page: 1,
    pageSize: 10,
    sortBy: 'LastLogin',
    sortDirection: 'desc',
  };

  // Role info hover card properties
  showRoleInfoHover = false;
  roleInfoHoverPosition = { x: 0, y: 0 };
  allPermissions: Permission[] = [];
  private roleInfoHoverTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.filterForm = this.formBuilder.group({
      isActive: [null],
    });
  }

  ngOnInit(): void {
    this.setupUserPermissionSubscription();
    this.loadRoles();
    this.loadPermissions();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
    }
  }

  /**
   * Initial page load: fetch the user list, the system-wide Super Admin count, and the
   * RESOURCE_VISIBILITY_MODE lookup together. The Super Admin count lets the table's first
   * render know whether a row is the last Super Admin (avoids briefly showing an enabled
   * dropdown); a failed count degrades to 0 rather than blocking the user list. The visibility
   * lookup supplies the Visibility column and snackbar labels — it is required, so if it fails
   * the page shows its (retryable) error state rather than rendering raw mode codes.
   */
  private loadInitialData(): void {
    this.isLoading = true;
    this.error = null;

    // Cancel any pending search request
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
    }

    this.pendingSearchRequest = forkJoin({
      users: this.userService.getUsersWithResources(this.currentRequest),
      superAdminCount: this.userService
        .getActiveSuperAdminCount()
        .pipe(catchError(() => of(0))),
      visibilityModes: this.lookupService.getLookupValues(
        LookupTypeCodes.RESOURCE_VISIBILITY_MODE,
      ),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ users, superAdminCount, visibilityModes }) => {
          this.users = users.items;
          this.totalCount = users.totalCount;
          this.dataSource.data = users.items;
          this.totalSuperAdminCount = superAdminCount;
          this.visibilityModeNames = new Map(
            visibilityModes.map((v) => [v.code, v.name]),
          );
          this.visibilityDefaultLabel =
            visibilityModes.find((v) => v.isDefault)?.name ?? 'System default';
          this.isLoading = false;
          this.isSearching = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading users:', error);
          this.error = 'Failed to load users. Please try again.';
          this.isLoading = false;
          this.isSearching = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadUsers(): void {
    this.isLoading = true;
    this.error = null;

    // Cancel any pending search request
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
    }

    this.pendingSearchRequest = this.userService
      .getUsersWithResources(this.currentRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.users = result.items;
          this.totalCount = result.totalCount;
          this.dataSource.data = result.items;
          this.isLoading = false;
          this.isSearching = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading users:', error);
          this.error = 'Failed to load users. Please try again.';
          this.isLoading = false;
          this.isSearching = false;
          this.cdr.detectChanges();
        },
      });
  }

  private setupUserPermissionSubscription(): void {
    // Subscribe to user roles changes to reactively update permissions
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe((roles) => {
        this.checkUserPermissions();
      });

    // Also check immediately in case roles are already loaded
    this.checkUserPermissions();
  }

  private checkUserPermissions(): void {
    this.isCurrentUserAdmin = this.authService.isAdmin();
    this.canManagePermissions = this.authService.canViewPermissionsManagement();
    this.displayedColumns = this.showActionsColumn
      ? [...this.baseColumns, 'actions']
      : [...this.baseColumns];
  }

  /** Whether the current user can manage practice-area assignments. */
  get canManagePracticeAreas(): boolean {
    return this.authService.canManagePracticeAreas();
  }

  /** Whether the current user can manage (secondary) office assignments. */
  get canManageOffices(): boolean {
    return this.authService.canManageSecondaryOffices();
  }

  /**
   * Show the Actions column when the user can manage practice areas or offices,
   * or is an admin (the admin-only Resource Visibility action also lives here).
   */
  get showActionsColumn(): boolean {
    return (
      this.canManagePracticeAreas ||
      this.canManageOffices ||
      this.isCurrentUserAdmin
    );
  }

  /**
   * Open the manage-practice-areas dialog for a user. Requires a linked
   * employee record (the assignment endpoints are keyed by employeeId).
   */
  openPracticeAreasDialog(user: UserDto): void {
    if (!user.employeeId) return;

    const dialogRef = this.dialog.open(UserPracticeAreasDialogComponent, {
      width: '540px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      data: {
        employeeId: user.employeeId,
        userName: this.getUserDisplayName(user),
        practiceAreas: user.practiceAreas ?? [],
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((changed) => {
        if (changed) {
          this.reloadUsersSilently();
        }
      });
  }

  /**
   * Open the manage-offices dialog for a user. Requires a linked employee
   * record (the assignment endpoints are keyed by employeeId).
   */
  openOfficesDialog(user: UserDto): void {
    if (!user.employeeId) return;

    const dialogRef = this.dialog.open(UserOfficesDialogComponent, {
      width: '540px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      data: {
        employeeId: user.employeeId,
        userName: this.getUserDisplayName(user),
        offices: user.offices ?? [],
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((changed) => {
        if (changed) {
          this.reloadUsersSilently();
        }
      });
  }

  /**
   * Check if a user has a protected admin role (Super Admin)
   */
  isUserProtectedAdmin(user: UserDto): boolean {
    return user.roles?.some((r) => r.roleName === 'Super Admin') ?? false;
  }

  /**
   * Load the total number of active Super Admins across the entire system.
   * This is a system-wide count (independent of pagination) so the "last
   * Super Admin" check is correct even when other Super Admins live on
   * different pages of the results.
   */
  private loadSuperAdminCount(): void {
    this.userService
      .getActiveSuperAdminCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (count) => {
          this.totalSuperAdminCount = count;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading Super Admin count:', error);
        },
      });
  }

  /**
   * Check if a user's role dropdown should be disabled
   * Disabled only if:
   * 1. The user is being updated
   * 2. The user is the LAST Super Admin (to prevent removing the last Super Admin)
   */
  isRoleDropdownDisabled(user: UserDto): boolean {
    const isUpdating = this.isUserRoleUpdating(user.id);

    // Disable if updating or if this is the last Super Admin
    return isUpdating || this.isLastSuperAdmin(user);
  }

  /**
   * Check if a user is the last Super Admin in the system
   * Used to show the help tooltip explaining why the dropdown is disabled.
   * Relies on the system-wide count so it stays correct across pages.
   */
  isLastSuperAdmin(user: UserDto): boolean {
    const isSuperAdmin = this.isUserProtectedAdmin(user);
    return isSuperAdmin && this.totalSuperAdminCount === 1;
  }

  private loadRoles(): void {
    this.roleService
      .getAllRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (roles) => {
          // Filter active roles and sort in desired order
          const roleOrder = [
            'Basic',
            'Staffing Partner',
            'PD Lead',
            'Admin',
            'Super Admin',
          ];

          // Filter out protected admin roles if user is not an admin
          let filteredRoles = roles.filter((role) => role.isActive);

          // If user is not an admin at all, exclude protected admin roles from view
          // Admins and Super Admins can view all roles (including Super Admin)
          if (!this.isCurrentUserAdmin) {
            filteredRoles = filteredRoles.filter(
              (role) => !role.isProtectedAdmin,
            );
          }

          this.roles = filteredRoles.sort((a, b) => {
            const indexA = roleOrder.indexOf(a.name);
            const indexB = roleOrder.indexOf(b.name);

            // If role not in order array, put it at the end
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
          });
        },
        error: (error) => {
          console.error('Error loading roles:', error);
        },
      });
  }

  private loadPermissions(): void {
    this.roleService
      .getAllPermissions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (permissions) => {
          this.allPermissions = permissions;
        },
        error: (error) => {
          console.error('Error loading permissions:', error);
        },
      });
  }

  onRoleChange(user: UserDto, newRoleId: number): void {
    if (newRoleId === this.getUserCurrentRoleId(user)) {
      return; // No change needed
    }

    // Set loading state
    const loadingKey = `user-role-${user.id}`;
    this.snackbarService.setLoading(loadingKey, true);
    this.updatingUserRoles.add(user.id);
    this.cdr.detectChanges();

    const newRole = this.roles.find((r) => r.id === newRoleId);
    const userName = `${user.firstName} ${user.lastName}`;

    // Check if user is updating their own role
    const isUpdatingSelf =
      user.email.toLowerCase() ===
      this.authService.getUserEmail().toLowerCase();

    this.userService
      .updateUserRole(user.id, newRoleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedUser) => {
          // Clear loading state
          this.snackbarService.setLoading(loadingKey, false);

          // Show success checkmark briefly
          this.snackbarService.showSuccessState(loadingKey, 2000);

          // Show success notification
          this.snackbarService.showSuccess(
            `Role updated to "${newRole?.name}" for ${userName}`,
          );

          // If user updated their own role, redirect to dashboard
          // Otherwise, reload the users list
          if (isUpdatingSelf) {
            // Give user time to see the success message before redirecting
            setTimeout(() => {
              this.router.navigate(['/dashboard']);
            }, 1500);
          } else {
            // Reload users without showing skeleton loader
            this.reloadUsersSilently(() => {
              this.updatingUserRoles.delete(user.id);
              this.cdr.detectChanges();
            });
          }
        },
        error: (error) => {
          console.error('Error updating user role:', error);

          // Clear loading state
          this.snackbarService.setLoading(loadingKey, false);

          const reason = describeApiError(error, '');

          // Check if trying to assign Super Admin role
          const isAssigningSuperAdmin = newRole?.name === 'Super Admin';

          if (isAssigningSuperAdmin && !this.canManagePermissions) {
            this.snackbarService.showError(
              'Error: an Admin is not allowed to change a user to a Super Admin.',
            );
          } else {
            this.snackbarService.showError(
              reason
                ? `Failed to update role: ${reason}`
                : 'Failed to update user role',
            );
          }

          // Reload users to revert dropdown to actual current role
          this.reloadUsersSilently(() => {
            this.updatingUserRoles.delete(user.id);
            this.cdr.detectChanges();
          });
        },
      });
  }

  /**
   * Display name for a row: the resource's first + last name, falling back to
   * the email when the record has no name populated (some directory rows come
   * from the external employee feed without a first/last name).
   */
  getUserDisplayName(user: UserDto): string {
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email;
  }

  /** True when the row has a real first/last name (vs. only an email). */
  hasDisplayName(user: UserDto): boolean {
    return !!`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  }

  getUserRoleNames(user: UserDto): string[] {
    if (!user.roles || user.roles.length === 0) {
      return [];
    }
    return user.roles
      .filter((role) => role.isActive)
      .map((role) => role.roleName);
  }

  getUserCurrentRoleId(user: UserDto): number | null {
    if (!user.roles || user.roles.length === 0) {
      return null;
    }
    const activeRole = user.roles.find((role) => role.isActive);
    return activeRole ? activeRole.roleId : null;
  }

  getUserCurrentRoleName(user: UserDto): string | null {
    if (!user.roles || user.roles.length === 0) {
      return null;
    }
    const activeRole = user.roles.find((role) => role.isActive);
    return activeRole ? activeRole.roleName : null;
  }

  /** Human-readable label for a visibility-mode code, from the RESOURCE_VISIBILITY_MODE lookup. */
  getVisibilityModeLabel(code: string): string {
    return this.visibilityModeNames.get(code) ?? code;
  }

  /**
   * The resource's visibility setting for the table column, read from the
   * server-provided override on the row. With no explicit per-resource override
   * (`visibilityModeCode` null) the resource uses the system default (the lookup
   * value flagged `isDefault`), flagged via `isDefault`.
   */
  getResourceVisibilityDisplay(user: UserDto): {
    label: string;
    isDefault: boolean;
  } {
    const code = user.visibilityModeCode;

    if (!code) {
      return {
        label: this.visibilityDefaultLabel,
        isDefault: true,
      };
    }

    // Prefer the server-provided name (already the lookup name) so the label is
    // correct even before the mode-name lookup finishes loading.
    return {
      label: user.visibilityModeName ?? this.getVisibilityModeLabel(code),
      isDefault: false,
    };
  }

  /**
   * Open the Resource Visibility dialog for a row and persist the admin's choice.
   * The dialog is seeded from the row's current override; on save an explicit mode
   * is written with a PUT and "System default" clears the override with a DELETE.
   * Requires a linked employee record (the endpoints are keyed by employeeId).
   */
  openVisibilityDialog(user: UserDto): void {
    if (!user.employeeId) return;
    const employeeId = user.employeeId;

    const currentSelection =
      user.visibilityModeCode ?? SYSTEM_DEFAULT_VISIBILITY_CODE;

    const dialogRef = this.dialog.open(ResourceVisibilityDialogComponent, {
      width: '540px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      autoFocus: false,
      data: {
        userName: this.getUserDisplayName(user),
        currentSelectionCode: currentSelection,
      } satisfies ResourceVisibilityDialogData,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedCode?: string) => {
        if (!selectedCode || selectedCode === currentSelection) {
          return; // Cancelled, closed, or unchanged.
        }

        const toDefault = selectedCode === SYSTEM_DEFAULT_VISIBILITY_CODE;
        const request$: Observable<unknown> = toDefault
          ? this.resourceService.clearResourceVisibility(employeeId)
          : this.resourceService.setResourceVisibility(employeeId, selectedCode);
        const label = toDefault
          ? 'System default'
          : this.getVisibilityModeLabel(selectedCode);

        request$.pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.snackbarService.showSuccess(
              `Visibility set to "${label}" for ${this.getUserDisplayName(user)}`,
            );
            // Refresh so the Visibility column reflects the persisted value.
            this.reloadUsersSilently();
          },
          error: (error) => {
            console.error('Error updating resource visibility:', error);
            this.snackbarService.showError(
              describeApiError(error, 'Failed to update resource visibility'),
            );
          },
        });
      });
  }

  isUserRoleUpdating(userId: number): boolean {
    return this.updatingUserRoles.has(userId);
  }

  private reloadUsersSilently(callback?: () => void): void {
    // Reload users without showing skeleton loader
    this.error = null;

    // Cancel any pending search request
    if (this.pendingSearchRequest) {
      this.pendingSearchRequest.unsubscribe();
    }

    this.pendingSearchRequest = this.userService
      .getUsersWithResources(this.currentRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.users = result.items;
          this.totalCount = result.totalCount;
          this.dataSource.data = result.items;

          // A role change may have added or removed a Super Admin, so refresh
          // the system-wide count that drives the "last Super Admin" guard.
          this.loadSuperAdminCount();
          this.cdr.detectChanges();

          // Call the callback after the UI has been updated
          if (callback) {
            callback();
          }
        },
        error: (error) => {
          console.error('Error reloading users:', error);
          this.error = 'Failed to reload users. Please try again.';
          this.cdr.detectChanges();

          // Call the callback even on error to re-enable the button
          if (callback) {
            callback();
          }
        },
      });
  }

  /**
   * Practice-area chips for a row, primary (sync-managed) first, coloured by primary/secondary.
   */
  getUserPracticeAreaChips(user: UserDto): MembershipChipItem[] {
    if (!user.practiceAreas?.length) {
      return [];
    }
    return user.practiceAreas
      .map((pa) => ({
        label: pa.practiceAreaName,
        isPrimary: pa.source === EmployeePracticeAreaSource.EmployeeSync,
      }))
      .sort(this.byPrimaryThenLabel);
  }

  /** Office chips for a row, primary office first, coloured by primary/secondary. */
  getUserOfficeChips(user: UserDto): MembershipChipItem[] {
    if (!user.offices?.length) {
      return [];
    }
    return user.offices
      .map((office) => ({
        label: office.officeName,
        isPrimary: office.isPrimary,
      }))
      .sort(this.byPrimaryThenLabel);
  }

  private byPrimaryThenLabel = (
    a: MembershipChipItem,
    b: MembershipChipItem,
  ): number => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return a.label.localeCompare(b.label);
  };

  formatLastLogin(lastLogin: string): string {
    const date = new Date(lastLogin);
    return date.toLocaleDateString();
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  private updateRequest(): void {
    const formValues = this.filterForm.value;

    this.currentRequest = {
      page: this.pageIndex + 1, // Convert to 1-based
      pageSize: this.pageSize,
      search: this.searchQuery?.trim() || undefined,
      isActive: formValues.isActive !== null ? formValues.isActive : undefined,
      sortBy: this.currentRequest.sortBy,
      sortDirection: this.currentRequest.sortDirection,
    };
  }

  onFilterChange(): void {
    // Reset to first page when filters change
    this.pageIndex = 0;
    this.updateRequest();

    // Show searching state for filter changes
    this.isSearching = true;
    this.loadUsers();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updateRequest();
    // Don't show searching state for pagination
    this.loadUsers();
  }

  onSortChange(sort: Sort): void {
    // Guard against undefined sort object
    if (!sort || !sort.active) {
      return;
    }

    // Map frontend sort fields to backend fields
    const sortFieldMap: { [key: string]: string } = {
      name: 'LastName',
      roles: 'LastName', // Sort by name when roles column is clicked
      practiceAreas: 'LastName', // Sort by name when practice areas column is clicked
      lastLogin: 'LastLogin',
      status: 'IsActive',
    };

    this.currentRequest.sortBy = sortFieldMap[sort.active] || 'LastLogin';
    this.currentRequest.sortDirection = sort.direction || 'asc';

    // Reset to first page when sorting changes
    this.pageIndex = 0;
    this.updateRequest();
    this.loadUsers();
  }

  retry(): void {
    this.loadInitialData();
  }

  onSearchInput(): void {
    // Debounce search input
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.onFilterChange();
    }, 300);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.onFilterChange();
  }

  resetFilters(): void {
    this.searchQuery = '';
    this.filterForm.reset({
      isActive: null,
    });

    // Reset sorting to default (LastLogin descending)
    this.currentRequest.sortBy = 'LastLogin';
    this.currentRequest.sortDirection = 'desc';
    const sort = this.sort();
    if (sort) {
      sort.active = 'lastLogin';
      sort.direction = 'desc';
      sort.sortChange.emit({ active: 'lastLogin', direction: 'desc' });
    }

    this.onFilterChange();
  }

  hasFiltersChanged(): boolean {
    const search = this.searchQuery?.trim();
    const isActive = this.filterForm.get('isActive')?.value;

    // Check if sorting differs from default (LastLogin desc)
    const sortingChanged =
      this.currentRequest.sortBy !== 'LastLogin' ||
      this.currentRequest.sortDirection !== 'desc';

    // Check if any filter differs from the default values
    return !!(search || isActive !== null || sortingChanged);
  }

  /**
   * Show role info hover card
   */
  showRoleInfoHoverCard(event: MouseEvent): void {
    // Clear any existing timeout
    if (this.roleInfoHoverTimeout) {
      clearTimeout(this.roleInfoHoverTimeout);
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const cardWidth = Math.min(viewportWidth * 0.95, 1200);

    this.roleInfoHoverPosition = {
      x: (viewportWidth - cardWidth) / 2,
      y: rect.bottom + 8, // 8px below the Role header
    };

    // Show with slight delay
    this.roleInfoHoverTimeout = setTimeout(() => {
      this.showRoleInfoHover = true;
      this.cdr.detectChanges();
    }, 300);
  }

  /**
   * Hide role info hover card
   */
  hideRoleInfoHoverCard(): void {
    // Clear timeout if hovering stopped before delay
    if (this.roleInfoHoverTimeout) {
      clearTimeout(this.roleInfoHoverTimeout);
    }

    this.showRoleInfoHover = false;
    this.cdr.detectChanges();
  }

  toRomanNumeral(index: number): string {
    const numerals = [
      'i',
      'ii',
      'iii',
      'iv',
      'v',
      'vi',
      'vii',
      'viii',
      'ix',
      'x',
      'xi',
      'xii',
      'xiii',
      'xiv',
      'xv',
      'xvi',
      'xvii',
      'xviii',
      'xix',
      'xx',
    ];
    return numerals[index] ?? String(index + 1);
  }

  /**
   * Display permissions based on the role's Permissions collection from the API.
   * Groups all available permissions by category and shows Yes/No for each based
   * on whether the role has that permission. Categories with no granted permissions
   * are shown as a single "No" entry with no sub-items.
   */
  getRolePermissions(role: Role): {
    label: string;
    value: string;
    subItems?: { label: string; value: string }[];
  }[] {
    const rolePermissionNames = new Set(
      role.permissions?.map((p) => p.name) ?? [],
    );

    const sorted = [...this.allPermissions].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );

    const byCategory = new Map<string, Permission[]>();
    for (const p of sorted) {
      const cat = p.category ?? 'Other';
      if (!byCategory.has(cat)) {
        byCategory.set(cat, []);
      }
      byCategory.get(cat)!.push(p);
    }

    return Array.from(byCategory.entries()).map(([category, permissions]) => {
      const hasAny = permissions.some((p) => rolePermissionNames.has(p.name));

      if (!hasAny) {
        return { label: category, value: 'No' };
      }

      return {
        label: category,
        value: 'Yes',
        subItems: permissions.map((p) => ({
          label: p.description,
          value: rolePermissionNames.has(p.name) ? 'Yes' : 'No',
        })),
      };
    });
  }
}
